import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Button, Paper, Typography, Snackbar } from '@mui/material';
import { CheckCircle as CompleteIcon } from '@mui/icons-material';
import { useTracking, useVideoTracking } from '../../../hooks/useTracking';

// YouTube IFrame API types
declare global {
    interface Window {
        YT: any;
        onYouTubeIframeAPIReady: (() => void) | undefined;
        Vimeo: any;
    }
}

interface VideoLessonPlayerProps {
    lesson: any;
    courseId?: number;
    userId: number;
    userEmail: string;
    onComplete: () => void;
}

// --- Helper: load external script once ---
function loadScript(src: string, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}

// --- Sub-component: HTML5 Video Player ---
const Html5VideoPlayer: React.FC<{
    videoSrc: string;
    resumePosition: number | null;
    onTimeUpdate: (currentTime: number, duration: number) => void;
    onPause: (currentTime: number, duration: number) => void;
    onEnded: () => void;
}> = ({ videoSrc, resumePosition, onTimeUpdate, onPause, onEnded }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hasResumedRef = useRef(false);

    // Resume position when video metadata is loaded
    const handleLoadedMetadata = useCallback(() => {
        if (videoRef.current && resumePosition && !hasResumedRef.current) {
            hasResumedRef.current = true;
            videoRef.current.currentTime = resumePosition;
        }
    }, [resumePosition]);

    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current) {
            const { currentTime, duration } = videoRef.current;
            if (duration > 0) {
                onTimeUpdate(currentTime, duration);
            }
        }
    }, [onTimeUpdate]);

    const handlePause = useCallback(() => {
        if (videoRef.current) {
            const { currentTime, duration } = videoRef.current;
            if (duration > 0) {
                onPause(currentTime, duration);
            }
        }
    }, [onPause]);

    // Save position on page leave
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (videoRef.current) {
                const { currentTime, duration } = videoRef.current;
                if (duration > 0) {
                    onPause(currentTime, duration);
                }
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [onPause]);

    return (
        <video
            ref={videoRef}
            controls
            style={{ width: '100%', maxHeight: '600px' }}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onPause={handlePause}
            onEnded={onEnded}
            src={videoSrc}
        >
            Ihr Browser unterstützt kein Video-Tag.
        </video>
    );
};

// --- Sub-component: YouTube Player ---
const YouTubePlayer: React.FC<{
    videoId: string;
    resumePosition: number | null;
    onTimeUpdate: (currentTime: number, duration: number) => void;
    onPause: (currentTime: number, duration: number) => void;
    onEnded: () => void;
}> = ({ videoId, resumePosition, onTimeUpdate, onPause, onEnded }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<any>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    const startPolling = useCallback(() => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(() => {
            if (playerRef.current?.getCurrentTime && playerRef.current?.getDuration) {
                const currentTime = playerRef.current.getCurrentTime();
                const duration = playerRef.current.getDuration();
                if (duration > 0) {
                    onTimeUpdate(currentTime, duration);
                }
            }
        }, 5000);
    }, [onTimeUpdate]);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    useEffect(() => {
        let mounted = true;

        const initPlayer = () => {
            if (!containerRef.current || !mounted) return;

            playerRef.current = new window.YT.Player(containerRef.current, {
                videoId,
                playerVars: {
                    autoplay: 0,
                    modestbranding: 1,
                    rel: 0,
                    start: resumePosition ? Math.floor(resumePosition) : undefined,
                },
                events: {
                    onReady: () => {
                        // If we have a resume position and didn't use start param
                        if (resumePosition && playerRef.current?.seekTo) {
                            playerRef.current.seekTo(resumePosition, true);
                        }
                    },
                    onStateChange: (event: any) => {
                        const YT = window.YT;
                        if (!playerRef.current?.getCurrentTime) return;
                        const currentTime = playerRef.current.getCurrentTime();
                        const duration = playerRef.current.getDuration();

                        switch (event.data) {
                            case YT.PlayerState.PLAYING:
                                startPolling();
                                break;
                            case YT.PlayerState.PAUSED:
                                stopPolling();
                                if (duration > 0) onPause(currentTime, duration);
                                break;
                            case YT.PlayerState.ENDED:
                                stopPolling();
                                onEnded();
                                break;
                        }
                    },
                },
            });
        };

        // Load YouTube IFrame API if not already loaded
        if (window.YT?.Player) {
            initPlayer();
        } else {
            const prevCallback = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                prevCallback?.();
                initPlayer();
            };
            loadScript('https://www.youtube.com/iframe_api', 'yt-iframe-api');
        }

        return () => {
            mounted = false;
            stopPolling();
            if (playerRef.current?.destroy) {
                playerRef.current.destroy();
            }
        };
    }, [videoId, resumePosition, startPolling, stopPolling, onPause, onEnded]);

    // Save on page leave
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (playerRef.current?.getCurrentTime && playerRef.current?.getDuration) {
                const currentTime = playerRef.current.getCurrentTime();
                const duration = playerRef.current.getDuration();
                if (duration > 0) onPause(currentTime, duration);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [onPause]);

    return (
        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
            <div
                ref={containerRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                }}
            />
        </Box>
    );
};

// --- Sub-component: Vimeo Player ---
const VimeoPlayer: React.FC<{
    videoId: string;
    resumePosition: number | null;
    onTimeUpdate: (currentTime: number, duration: number) => void;
    onPause: (currentTime: number, duration: number) => void;
    onEnded: () => void;
}> = ({ videoId, resumePosition, onTimeUpdate, onPause, onEnded }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<any>(null);
    const durationRef = useRef<number>(0);
    const currentTimeRef = useRef<number>(0);

    useEffect(() => {
        let mounted = true;

        const initPlayer = async () => {
            if (!containerRef.current || !mounted) return;

            const player = new window.Vimeo.Player(containerRef.current, {
                id: videoId,
                responsive: true,
            });
            playerRef.current = player;

            player.getDuration().then((d: number) => {
                durationRef.current = d;
            });

            // Resume position
            player.on('loaded', () => {
                if (resumePosition && resumePosition > 0) {
                    player.setCurrentTime(resumePosition);
                }
            });

            player.on('timeupdate', (data: { seconds: number; duration: number }) => {
                currentTimeRef.current = data.seconds;
                durationRef.current = data.duration;
                onTimeUpdate(data.seconds, data.duration);
            });

            player.on('pause', (data: { seconds: number; duration: number }) => {
                onPause(data.seconds, data.duration);
            });

            player.on('ended', () => {
                onEnded();
            });
        };

        // Load Vimeo Player SDK
        if (window.Vimeo?.Player) {
            initPlayer();
        } else {
            loadScript('https://player.vimeo.com/api/player.js', 'vimeo-player-sdk')
                .then(initPlayer)
                .catch(console.error);
        }

        return () => {
            mounted = false;
            if (playerRef.current?.destroy) {
                playerRef.current.destroy();
            }
        };
    }, [videoId, resumePosition, onTimeUpdate, onPause, onEnded]);

    // Save on page leave
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (durationRef.current > 0) {
                onPause(currentTimeRef.current, durationRef.current);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [onPause]);

    return <div ref={containerRef} />;
};

// --- Main Component ---
const VideoLessonPlayer: React.FC<VideoLessonPlayerProps> = ({
    lesson,
    courseId,
    userId,
    userEmail,
    onComplete
}) => {
    const [completed, setCompleted] = useState(false);
    const [videoSrc, setVideoSrc] = useState<string>('');
    const [showResumeNotice, setShowResumeNotice] = useState(false);
    const content = lesson.content || {};

    // Initialize tracking
    const {
        startLesson,
        completeLesson,
        isLoading: trackingLoading,
        error: trackingError
    } = useTracking({
        lessonId: lesson.id,
        lessonTitle: lesson.title,
        courseId,
        userId,
        userEmail,
    });

    // Video-specific tracking with resume
    const { trackProgress, savePositionNow, resumePosition } = useVideoTracking(
        lesson.id,
        lesson.title,
        userId,
        userEmail
    );

    // Start lesson tracking on mount
    useEffect(() => {
        startLesson();
    }, [startLesson]);

    // Show resume notice when position is loaded
    useEffect(() => {
        if (resumePosition && resumePosition > 5) {
            setShowResumeNotice(true);
        }
    }, [resumePosition]);

    // Support both videoUrl (from media library) and externalUrl (external URLs)
    let videoUrl = content.videoUrl || content.externalUrl;
    let videoType = content.videoType || 'url';
    let isMediaLibrary = false;

    // If videoUrl is from media library (starts with /api/media/), fix the route
    if (videoUrl && videoUrl.startsWith('/api/media/')) {
        isMediaLibrary = true;
        const uuid = videoUrl.split('/').pop();
        videoUrl = uuid; // Store UUID for later use
    }

    // Auto-detect YouTube or Vimeo URLs
    if (videoUrl && typeof videoUrl === 'string') {
        if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
            videoType = 'youtube';
            const match = videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
            if (match) {
                videoUrl = match[1];
            }
        } else if (videoUrl.includes('vimeo.com')) {
            videoType = 'vimeo';
            const match = videoUrl.match(/vimeo\.com\/(\d+)/);
            if (match) {
                videoUrl = match[1];
            }
        }
    }

    // Load video from media library with authentication
    useEffect(() => {
        if (isMediaLibrary && videoUrl) {
            const fetchVideo = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/preview/${videoUrl}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        setVideoSrc(url);
                    } else {
                        console.error('Failed to load video:', response.status);
                    }
                } catch (error) {
                    console.error('Error loading video:', error);
                }
            };
            fetchVideo();
        } else if (!isMediaLibrary && videoUrl) {
            setVideoSrc(videoUrl);
        }

        // Cleanup blob URL on unmount
        return () => {
            if (videoSrc && videoSrc.startsWith('blob:')) {
                URL.revokeObjectURL(videoSrc);
            }
        };
    }, [videoUrl, isMediaLibrary]);

    const handleTimeUpdate = useCallback((currentTime: number, duration: number) => {
        trackProgress(currentTime, duration);
    }, [trackProgress]);

    const handlePause = useCallback((currentTime: number, duration: number) => {
        savePositionNow(currentTime, duration);
    }, [savePositionNow]);

    const handleVideoEnd = useCallback(() => {
        setCompleted(true);
    }, []);

    const handleMarkComplete = async () => {
        try {
            await completeLesson({ success: true });
            onComplete();
        } catch (error) {
            console.error('Error completing lesson:', error);
            onComplete();
        }
    };

    const formatTime = (seconds: number): string => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                {content.description && (
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {content.description}
                    </Typography>
                )}

                {(videoUrl || videoSrc) ? (
                    <Box sx={{ mt: 2 }}>
                        {videoType === 'url' && videoSrc && (
                            <Html5VideoPlayer
                                videoSrc={videoSrc}
                                resumePosition={resumePosition}
                                onTimeUpdate={handleTimeUpdate}
                                onPause={handlePause}
                                onEnded={handleVideoEnd}
                            />
                        )}

                        {videoType === 'youtube' && videoUrl && (
                            <YouTubePlayer
                                videoId={videoUrl}
                                resumePosition={resumePosition}
                                onTimeUpdate={handleTimeUpdate}
                                onPause={handlePause}
                                onEnded={handleVideoEnd}
                            />
                        )}

                        {videoType === 'vimeo' && videoUrl && (
                            <VimeoPlayer
                                videoId={videoUrl}
                                resumePosition={resumePosition}
                                onTimeUpdate={handleTimeUpdate}
                                onPause={handlePause}
                                onEnded={handleVideoEnd}
                            />
                        )}
                    </Box>
                ) : (
                    <Typography color="text.secondary">
                        Kein Video konfiguriert
                    </Typography>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<CompleteIcon />}
                    onClick={handleMarkComplete}
                >
                    Als abgeschlossen markieren
                </Button>
            </Box>

            <Snackbar
                open={showResumeNotice}
                autoHideDuration={4000}
                onClose={() => setShowResumeNotice(false)}
                message={`Video wird ab ${formatTime(resumePosition || 0)} fortgesetzt`}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default VideoLessonPlayer;
